import dayjs, {Dayjs} from "dayjs";
import {PUBLIC_REGISTRATION_DRAFT_STORAGE_PREFIX, PUBLIC_REGISTRATION_DRAFT_VERSION, PayerTypeValue, PaymentMethodCategoryValue, PaymentMethodValue, PublicEventFormResponse, PublicFormField, PublicRegistrationBulkRowValues, PublicRegistrationDraft, PublicRegistrationFormValues, PublicRegistrationMode, PublicRegistrationOption} from "./types";
import {buildActivityAnswerInitialValues, buildDynamicAnswerInitialValues, buildInitialBulkRegistrationRowValues, buildInitialFormValues, buildRoomAvailabilityOptionKey, findRoomAvailability, getActivePublicRegistrationChoiceIds, getPaymentMethodsForCategory, normalizePaymentMethodValue, normalizePublicFormFieldType, normalizePublicRegistrationOptionQuestionType} from "./publicRegistrationModel";

export const buildPublicRegistrationDraftStorageKey = (eventIdentifier: string): string =>
    `${PUBLIC_REGISTRATION_DRAFT_STORAGE_PREFIX}:${encodeURIComponent(eventIdentifier.trim().toLowerCase())}`;

const asDayjsOrUndefined = (value: unknown): Dayjs | undefined => {
    if (!value) {
        return undefined;
    }

    const parsed = dayjs.isDayjs(value) ? value : dayjs(value as any);
    return parsed.isValid() ? parsed : undefined;
};

const restoreDynamicAnswerValues = (
    fields: PublicFormField[] | undefined,
    rawAnswers: Record<string, any> | undefined
): Record<string, unknown> => {
    const defaultAnswers = buildDynamicAnswerInitialValues(fields);

    (fields ?? []).forEach(field => {
        if (!rawAnswers || !Object.prototype.hasOwnProperty.call(rawAnswers, field.code)) {
            return;
        }

        const rawValue = rawAnswers[field.code];
        const fieldType = normalizePublicFormFieldType(field.type);

        if (fieldType === "Date") {
            defaultAnswers[field.code] = asDayjsOrUndefined(rawValue);
        } else if (fieldType === "MultiSelect") {
            defaultAnswers[field.code] = Array.isArray(rawValue) ? rawValue : [];
        } else if (fieldType === "Checkbox") {
            defaultAnswers[field.code] = !!rawValue;
        } else if (fieldType === "Number") {
            const numericValue = Number(rawValue);
            defaultAnswers[field.code] = Number.isFinite(numericValue) ? numericValue : undefined;
        } else {
            defaultAnswers[field.code] = rawValue;
        }
    });

    return defaultAnswers;
};


const restoreActivityAnswerValues = (
    options: PublicRegistrationOption[] | undefined,
    rawActivityAnswers: Record<string, any> | undefined
): Record<string, unknown> => {
    const defaultAnswers = buildActivityAnswerInitialValues(options);

    (options ?? []).forEach(option => {
        const key = String(option.id);
        if (!rawActivityAnswers || !Object.prototype.hasOwnProperty.call(rawActivityAnswers, key)) {
            return;
        }

        const questionType = normalizePublicRegistrationOptionQuestionType(option.questionType);
        const rawValue = rawActivityAnswers[key];

        if (questionType === "SingleChoice") {
            const choiceId = Number(rawValue);
            const allowedChoiceIds = getActivePublicRegistrationChoiceIds(option);
            defaultAnswers[key] = allowedChoiceIds.has(choiceId) ? choiceId : undefined;
        } else if (questionType === "MultiChoice") {
            const allowedChoiceIds = getActivePublicRegistrationChoiceIds(option);
            defaultAnswers[key] = Array.isArray(rawValue)
                ? rawValue
                    .map(Number)
                    .filter((choiceId: number) => allowedChoiceIds.has(choiceId))
                : [];
        } else if (questionType === "Text") {
            defaultAnswers[key] = typeof rawValue === "string" ? rawValue : "";
        }
    });

    return defaultAnswers;
};

const restoreBulkRows = (
    form: PublicEventFormResponse,
    rawRows: unknown
): PublicRegistrationBulkRowValues[] => {
    const defaultRow = buildInitialBulkRegistrationRowValues(form.template?.fields, form.registrationOptions);
    const optionIds = new Set((form.registrationOptions ?? []).map(option => option.id));

    if (!Array.isArray(rawRows) || rawRows.length === 0) {
        return [defaultRow];
    }

    return rawRows.map(row => {
        const rawRow = (row ?? {}) as PublicRegistrationBulkRowValues;

        return {
            ...defaultRow,
            ...rawRow,
            answers: restoreDynamicAnswerValues(form.template?.fields, rawRow.answers),
            selectedOptionIds: (rawRow.selectedOptionIds ?? [])
                .map(Number)
                .filter(optionId => optionIds.has(optionId)),
            activityAnswers: restoreActivityAnswerValues(form.registrationOptions, rawRow.activityAnswers)
        };
    });
};

const restorePaymentMethod = (
    value: unknown,
    allowedValues: PaymentMethodValue[],
    fallback: PaymentMethodValue | undefined
): PaymentMethodValue | undefined => {
    if (value === undefined || value === null) {
        return fallback;
    }

    const normalized = normalizePaymentMethodValue(value as string | number);
    return allowedValues.includes(normalized) ? normalized : fallback;
};

const restorePayerType = (value: unknown): PayerTypeValue => {
    const normalized = Number(value);
    return Object.values(PayerTypeValue).includes(normalized as PayerTypeValue)
        ? normalized as PayerTypeValue
        : PayerTypeValue.Individual;
};

const restorePublicRegistrationDraftValues = (
    form: PublicEventFormResponse,
    rawValues?: Record<string, any>
): PublicRegistrationFormValues => {
    const defaultValues = buildInitialFormValues(form);
    if (!rawValues) {
        return defaultValues;
    }

    const participationFeeIds = new Set((form.participationFees ?? []).map(fee => fee.feePriceId));
    const rawParticipationFeeId = Number(rawValues.selectedParticipationFeePriceId);
    const selectedParticipationFeePriceId = form.hasParticipationFee !== false && participationFeeIds.has(rawParticipationFeeId)
        ? rawParticipationFeeId
        : defaultValues.selectedParticipationFeePriceId;

    const optionIds = new Set((form.registrationOptions ?? []).map(option => option.id));
    const selectedOptionIds = Array.isArray(rawValues.selectedOptionIds)
        ? rawValues.selectedOptionIds
            .map(Number)
            .filter((optionId: number) => optionIds.has(optionId))
        : defaultValues.selectedOptionIds;

    const selectedRoom = findRoomAvailability(form.roomAvailability, rawValues.accommodationSelectionKey);
    const participationMethods = getPaymentMethodsForCategory(form.paymentMethods, PaymentMethodCategoryValue.Participation);
    const accommodationMethods = getPaymentMethodsForCategory(form.paymentMethods, PaymentMethodCategoryValue.Accommodation);
    const rawValuesWithoutDueDate = {...rawValues};
    delete rawValuesWithoutDueDate.dueDate;

    return {
        ...defaultValues,
        ...rawValuesWithoutDueDate,
        payerType: restorePayerType(rawValues.payerType),
        answers: restoreDynamicAnswerValues(form.template?.fields, rawValues.answers),
        activityAnswers: restoreActivityAnswerValues(form.registrationOptions, rawValues.activityAnswers),
        selectedParticipationFeePriceId,
        selectedOptionIds,
        participationPaymentMethod: restorePaymentMethod(
            rawValues.participationPaymentMethod,
            participationMethods,
            defaultValues.participationPaymentMethod),
        reserveAccommodation: !!rawValues.reserveAccommodation && !!selectedRoom,
        accommodationSelectionKey: selectedRoom
            ? buildRoomAvailabilityOptionKey(selectedRoom)
            : defaultValues.accommodationSelectionKey,
        accommodationArrivalDate: asDayjsOrUndefined(rawValues.accommodationArrivalDate) ?? defaultValues.accommodationArrivalDate,
        accommodationDepartureDate: asDayjsOrUndefined(rawValues.accommodationDepartureDate) ?? defaultValues.accommodationDepartureDate,
        accommodationRoomCount: Number(rawValues.accommodationRoomCount) > 0
            ? Number(rawValues.accommodationRoomCount)
            : defaultValues.accommodationRoomCount,
        accommodationPaymentMethod: restorePaymentMethod(
            rawValues.accommodationPaymentMethod,
            accommodationMethods,
            defaultValues.accommodationPaymentMethod),
        gdprConsentAccepted: rawValues.gdprConsentAccepted === true,
        bulkRows: restoreBulkRows(form, rawValues.bulkRows)
    };
};

export const serializePublicRegistrationDraft = (
    values: PublicRegistrationFormValues,
    mode: PublicRegistrationMode
): string => JSON.stringify({
    version: PUBLIC_REGISTRATION_DRAFT_VERSION,
    mode,
    values,
    savedAtUtc: new Date().toISOString()
});

export const deserializePublicRegistrationDraft = (
    serializedDraft: string | null | undefined,
    form: PublicEventFormResponse
): { mode: PublicRegistrationMode; values: PublicRegistrationFormValues } | null => {
    if (!serializedDraft) {
        return null;
    }

    try {
        const draft = JSON.parse(serializedDraft) as PublicRegistrationDraft;
        if (draft.version !== PUBLIC_REGISTRATION_DRAFT_VERSION) {
            return null;
        }

        return {
            mode: draft.mode === "bulk" ? "bulk" : "single",
            values: restorePublicRegistrationDraftValues(form, draft.values)
        };
    } catch {
        return null;
    }
};

export const readPublicRegistrationDraft = (
    storageKey: string | undefined,
    form: PublicEventFormResponse
) => {
    if (!storageKey || typeof window === "undefined") {
        return null;
    }

    try {
        return deserializePublicRegistrationDraft(window.localStorage.getItem(storageKey), form);
    } catch {
        return null;
    }
};

export const writePublicRegistrationDraft = (
    storageKey: string | undefined,
    values: PublicRegistrationFormValues,
    mode: PublicRegistrationMode
) => {
    if (!storageKey || typeof window === "undefined") {
        return;
    }

    try {
        window.localStorage.setItem(storageKey, serializePublicRegistrationDraft(values, mode));
    } catch {
        // Browser storage can be disabled or full; losing the draft should not block registration.
    }
};

export const clearPublicRegistrationDraft = (storageKey: string | undefined) => {
    if (!storageKey || typeof window === "undefined") {
        return;
    }

    try {
        window.localStorage.removeItem(storageKey);
    } catch {
        // Ignore storage cleanup failures.
    }
};
